import { useState, useEffect, useRef } from 'react';
import { useApi } from '../contexts/ApiContext';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../contexts/ToastContext';
import { formatDateTime } from '../utils/dateFormatter';
import './CommentsSection.css';

export type CommentEntityType = 
  | 'WORK_ORDER' 
  | 'PURCHASE_ORDER_FUNDICION' 
  | 'PURCHASE_ORDER_PIEDRAS' 
  | 'COATING_ORDER'
  | 'METAL_PIECE'
  | 'STONE';

interface CommentAttachment {
  id: number;
  comment_id: number;
  filename: string;
  original_filename: string;
  mime_type: string;
  size: number;
  url: string;
  created_at: string;
}

interface CommentReaction {
  id: number;
  comment_id: number;
  user_id: number;
  username?: string;
  reaction_type: 'like' | 'love' | 'check' | 'thumbs_up';
  created_at: string;
}

interface Comment {
  id: number;
  entity_type: CommentEntityType;
  entity_id: number;
  user_id: number;
  username?: string;
  content: string;
  mentions?: number[];
  attachments?: CommentAttachment[];
  reactions?: CommentReaction[];
  is_edited: boolean;
  created_at: string;
  updated_at: string;
}

interface MentionableUser {
  id: number;
  username: string;
}

interface CommentsSectionProps {
  entityType: CommentEntityType;
  entityId: number;
  title?: string;
  showTitle?: boolean;
}

export default function CommentsSection({
  entityType,
  entityId,
  title = 'Comentarios',
  showTitle = true,
}: CommentsSectionProps) {
  const { api } = useApi();
  const { user } = useAuth();
  const { showSuccess, showError } = useToast();
  
  const [comments, setComments] = useState<Comment[]>([]);
  const [loading, setLoading] = useState(true);
  const [newComment, setNewComment] = useState('');
  const [editingCommentId, setEditingCommentId] = useState<number | null>(null);
  const [editingContent, setEditingContent] = useState('');
  const [mentionableUsers, setMentionableUsers] = useState<MentionableUser[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const mentionsRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    loadComments();
    loadMentionableUsers();
  }, [entityType, entityId]);

  // Cerrar menú de menciones al hacer click fuera
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (mentionsRef.current && !mentionsRef.current.contains(event.target as Node)) {
        setShowMentions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadComments = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/comments/${entityType}/${entityId}`);
      setComments(response.data);
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al cargar comentarios');
    } finally {
      setLoading(false);
    }
  };

  const loadMentionableUsers = async () => {
    try {
      const response = await api.get('/comments/users/mentionable');
      setMentionableUsers(response.data);
    } catch (err: any) {
      console.error('Error al cargar usuarios mencionables:', err);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = Array.from(e.target.files || []);
    if (files.length > 5) {
      showError('Máximo 5 archivos permitidos');
      return;
    }

    setSelectedFiles(files);
    
    // Crear previews para imágenes
    const previews: string[] = [];
    files.forEach(file => {
      if (file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = (e) => {
          previews.push(e.target?.result as string);
          if (previews.length === files.filter(f => f.type.startsWith('image/')).length) {
            setFilePreviews(previews);
          }
        };
        reader.readAsDataURL(file);
      }
    });
  };

  const handleRemoveFile = (index: number) => {
    const newFiles = selectedFiles.filter((_, i) => i !== index);
    setSelectedFiles(newFiles);
    setFilePreviews(filePreviews.filter((_, i) => i !== index));
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmitComment = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newComment.trim() && selectedFiles.length === 0) return;

    try {
      const formData = new FormData();
      formData.append('entity_type', entityType);
      formData.append('entity_id', entityId.toString());
      formData.append('content', newComment.trim() || '');
      
      selectedFiles.forEach(file => {
        formData.append('attachments', file);
      });

      await api.post('/comments', formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      
      setNewComment('');
      setSelectedFiles([]);
      setFilePreviews([]);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      showSuccess('Comentario agregado correctamente');
      loadComments();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al agregar comentario');
    }
  };

  const handleStartEdit = (comment: Comment) => {
    setEditingCommentId(comment.id);
    setEditingContent(comment.content);
  };

  const handleCancelEdit = () => {
    setEditingCommentId(null);
    setEditingContent('');
  };

  const handleSaveEdit = async (commentId: number) => {
    if (!editingContent.trim()) return;

    try {
      await api.put(`/comments/${commentId}`, {
        content: editingContent.trim(),
      });
      setEditingCommentId(null);
      setEditingContent('');
      showSuccess('Comentario actualizado correctamente');
      loadComments();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al actualizar comentario');
    }
  };

  const handleDeleteComment = async (commentId: number) => {
    if (!confirm('¿Estás seguro de que quieres eliminar este comentario?')) {
      return;
    }

    try {
      await api.delete(`/comments/${commentId}`);
      showSuccess('Comentario eliminado correctamente');
      loadComments();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al eliminar comentario');
    }
  };

  const handleMentionClick = (username: string) => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    const cursorPos = textarea.selectionStart;
    const textBefore = newComment.substring(0, cursorPos);
    const textAfter = newComment.substring(cursorPos);
    
    // Verificar si ya hay un @ antes del cursor
    const lastAt = textBefore.lastIndexOf('@');
    if (lastAt !== -1 && !textBefore.substring(lastAt + 1).includes(' ')) {
      // Reemplazar desde el @ hasta el cursor
      const newText = textBefore.substring(0, lastAt + 1) + username + ' ' + textAfter;
      setNewComment(newText);
      setShowMentions(false);
      
      // Restaurar foco y posición del cursor
      setTimeout(() => {
        textarea.focus();
        const newCursorPos = lastAt + 1 + username.length + 1;
        textarea.setSelectionRange(newCursorPos, newCursorPos);
      }, 0);
    } else {
      // Agregar @username al final
      setNewComment(newComment + `@${username} `);
      setShowMentions(false);
    }
  };

  const handleCommentInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    setNewComment(value);

    // Mostrar menú de menciones cuando se escribe @
    const cursorPos = e.target.selectionStart;
    const textBefore = value.substring(0, cursorPos);
    const lastAt = textBefore.lastIndexOf('@');
    
    if (lastAt !== -1 && !textBefore.substring(lastAt + 1).includes(' ')) {
      const searchTerm = textBefore.substring(lastAt + 1).toLowerCase();
      setShowMentions(true);
    } else {
      setShowMentions(false);
    }
  };

  const formatCommentContent = (content: string) => {
    // Resaltar menciones (@username)
    const mentionRegex = /@(\w+)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = mentionRegex.exec(content)) !== null) {
      // Agregar texto antes de la mención
      if (match.index > lastIndex) {
        parts.push(content.substring(lastIndex, match.index));
      }
      
      // Agregar mención resaltada
      parts.push(
        <span key={match.index} className="comment-mention">
          @{match[1]}
        </span>
      );
      
      lastIndex = match.index + match[0].length;
    }

    // Agregar texto restante
    if (lastIndex < content.length) {
      parts.push(content.substring(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  };

  const handleReaction = async (commentId: number, reactionType: 'like' | 'love' | 'check' | 'thumbs_up') => {
    if (!user) return;

    try {
      const comment = comments.find(c => c.id === commentId);
      const existingReaction = comment?.reactions?.find(
        r => r.user_id === user.id && r.reaction_type === reactionType
      );

      if (existingReaction) {
        // Eliminar reacción
        await api.delete(`/comments/${commentId}/reactions`, {
          data: { reaction_type: reactionType },
        });
      } else {
        // Agregar reacción
        await api.post(`/comments/${commentId}/reactions`, {
          reaction_type: reactionType,
        });
      }
      loadComments();
    } catch (err: any) {
      showError(err.response?.data?.error || 'Error al agregar reacción');
    }
  };

  const getReactionEmoji = (type: string): string => {
    const emojiMap: Record<string, string> = {
      like: '👍',
      love: '❤️',
      check: '✅',
      thumbs_up: '👍',
    };
    return emojiMap[type] || '👍';
  };

  const filteredMentionableUsers = mentionableUsers.filter(u => {
    if (!showMentions) return false;
    const cursorPos = textareaRef.current?.selectionStart || 0;
    const textBefore = newComment.substring(0, cursorPos);
    const lastAt = textBefore.lastIndexOf('@');
    if (lastAt === -1) return false;
    const searchTerm = textBefore.substring(lastAt + 1).toLowerCase();
    return u.username.toLowerCase().includes(searchTerm);
  });

  return (
    <div className="comments-section">
      {showTitle && <h3 className="comments-section-title">{title}</h3>}

      {/* Lista de comentarios */}
      <div className="comments-list">
        {loading ? (
          <div className="comments-loading">Cargando comentarios...</div>
        ) : comments.length === 0 ? (
          <div className="comments-empty">No hay comentarios aún. Sé el primero en comentar.</div>
        ) : (
          comments.map((comment) => (
            <div key={comment.id} className="comment-item">
              <div className="comment-header">
                <div className="comment-author">
                  <strong>{comment.username || 'Usuario'}</strong>
                  {comment.is_edited && (
                    <span className="comment-edited" title="Editado">(editado)</span>
                  )}
                </div>
                <div className="comment-date">
                  {formatDateTime(comment.created_at)}
                </div>
              </div>
              
              {editingCommentId === comment.id ? (
                <div className="comment-edit">
                  <textarea
                    className="comment-edit-input"
                    value={editingContent}
                    onChange={(e) => setEditingContent(e.target.value)}
                    rows={3}
                  />
                  <div className="comment-edit-actions">
                    <button
                      className="btn btn-sm btn-primary"
                      onClick={() => handleSaveEdit(comment.id)}
                    >
                      Guardar
                    </button>
                    <button
                      className="btn btn-sm btn-secondary"
                      onClick={handleCancelEdit}
                    >
                      Cancelar
                    </button>
                  </div>
                </div>
              ) : (
                <>
                  <div className="comment-content">
                    {formatCommentContent(comment.content)}
                  </div>
                  
                  {/* Archivos adjuntos */}
                  {comment.attachments && comment.attachments.length > 0 && (
                    <div className="comment-attachments">
                      {comment.attachments.map((attachment) => (
                        <div key={attachment.id} className="comment-attachment">
                          {attachment.mime_type.startsWith('image/') ? (
                            <img 
                              src={attachment.url} 
                              alt={attachment.original_filename}
                              className="comment-attachment-image"
                              onClick={() => window.open(attachment.url, '_blank')}
                            />
                          ) : (
                            <a 
                              href={attachment.url} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="comment-attachment-file"
                            >
                              📎 {attachment.original_filename}
                              <span className="comment-attachment-size">
                                ({(attachment.size / 1024).toFixed(1)} KB)
                              </span>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Reacciones */}
                  <div className="comment-reactions">
                    <div className="comment-reactions-buttons">
                      <button
                        type="button"
                        className={`comment-reaction-btn ${comment.reactions?.some(r => r.user_id === user?.id && r.reaction_type === 'like') ? 'active' : ''}`}
                        onClick={() => handleReaction(comment.id, 'like')}
                        title="Me gusta"
                      >
                        👍
                      </button>
                      <button
                        type="button"
                        className={`comment-reaction-btn ${comment.reactions?.some(r => r.user_id === user?.id && r.reaction_type === 'love') ? 'active' : ''}`}
                        onClick={() => handleReaction(comment.id, 'love')}
                        title="Me encanta"
                      >
                        ❤️
                      </button>
                      <button
                        type="button"
                        className={`comment-reaction-btn ${comment.reactions?.some(r => r.user_id === user?.id && r.reaction_type === 'check') ? 'active' : ''}`}
                        onClick={() => handleReaction(comment.id, 'check')}
                        title="Confirmado"
                      >
                        ✅
                      </button>
                    </div>
                    {comment.reactions && comment.reactions.length > 0 && (
                      <div className="comment-reactions-list">
                        {Object.entries(
                          comment.reactions.reduce((acc, r) => {
                            acc[r.reaction_type] = (acc[r.reaction_type] || 0) + 1;
                            return acc;
                          }, {} as Record<string, number>)
                        ).map(([type, count]) => (
                          <span key={type} className="comment-reaction-count">
                            {getReactionEmoji(type)} {count}
                          </span>
                        ))}
                      </div>
                    )}
                  </div>
                </>
              )}

              {user && user.id === comment.user_id && editingCommentId !== comment.id && (
                <div className="comment-actions">
                  <button
                    className="comment-action-btn"
                    onClick={() => handleStartEdit(comment)}
                    title="Editar"
                  >
                    ✏️ Editar
                  </button>
                  <button
                    className="comment-action-btn comment-action-delete"
                    onClick={() => handleDeleteComment(comment.id)}
                    title="Eliminar"
                  >
                    🗑️ Eliminar
                  </button>
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Formulario para nuevo comentario */}
      <form className="comments-form" onSubmit={handleSubmitComment}>
        <div className="comments-input-wrapper" ref={mentionsRef}>
          <textarea
            ref={textareaRef}
            className="comments-input"
            value={newComment}
            onChange={handleCommentInputChange}
            placeholder="Escribe un comentario... Usa @ para mencionar usuarios"
            rows={3}
          />
          {showMentions && filteredMentionableUsers.length > 0 && (
            <div className="mentions-dropdown">
              {filteredMentionableUsers.map((user) => (
                <button
                  key={user.id}
                  type="button"
                  className="mention-item"
                  onClick={() => handleMentionClick(user.username)}
                >
                  @{user.username}
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Vista previa de archivos seleccionados */}
        {selectedFiles.length > 0 && (
          <div className="comment-files-preview">
            {selectedFiles.map((file, index) => (
              <div key={index} className="comment-file-preview-item">
                {file.type.startsWith('image/') && filePreviews[index] ? (
                  <img src={filePreviews[index]} alt={file.name} className="comment-file-preview-image" />
                ) : (
                  <span className="comment-file-preview-icon">📎</span>
                )}
                <span className="comment-file-preview-name">{file.name}</span>
                <button
                  type="button"
                  className="comment-file-preview-remove"
                  onClick={() => handleRemoveFile(index)}
                >
                  ✕
                </button>
              </div>
            ))}
          </div>
        )}

        <div className="comments-form-actions">
          <input
            ref={fileInputRef}
            type="file"
            id="comment-file-input"
            multiple
            accept="image/*,.pdf,.doc,.docx,.txt"
            onChange={handleFileSelect}
            style={{ display: 'none' }}
          />
          <label htmlFor="comment-file-input" className="btn btn-secondary comments-attach-btn">
            📎 Adjuntar archivo
          </label>
          <button
            type="submit"
            className="btn btn-primary comments-submit"
            disabled={!newComment.trim() && selectedFiles.length === 0}
          >
            Comentar
          </button>
        </div>
      </form>
    </div>
  );
}

